import { useState, type FormEvent, type ReactNode } from 'react';
import { useAuth } from '@repletoryreach/sdk-auth';
import { Stack, Card, Title, Button, Text, Group, Avatar } from '@repletoryreach/sdk-ui';
import { PageHeader, StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import { TextInput, Select } from '@repletoryreach/sdk-ui/inputs';
import { identity } from '@repletoryreach/sdk-api';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { NotificationPrefsPanel } from './components/NotificationPrefsPanel';
import { useT } from '../../../i18n/I18nProvider';
import './profile.css';

/*
 * The admin console is English by design, so this is a formatting selector,
 * not a language selector: it decides how dates, numbers and currency read.
 * It used to be free text, and "banana" saved happily.
 */
const LOCALES = ['en-ZA', 'en-GB', 'en-US', 'en-NG'];

export default function SettingsProfilePage() {
  const t = useT();
  const { user, refresh } = useAuth();

  const initialName = user?.display_name ?? '';
  const initialLocale = user?.locale ?? 'en-ZA';
  const [displayName, setDisplayName] = useState(initialName);
  const [locale, setLocale] = useState(initialLocale);
  const [busy, setBusy] = useState(false);

  const dirty = displayName.trim() !== initialName.trim() || locale !== initialLocale;

  // Two initials where a name gives them, matching the avatar the header shows,
  // so the same person is drawn the same way in both places.
  const avatarSource = user?.display_name || user?.email || '?';
  const initials = avatarSource.includes(' ')
    ? avatarSource.split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase()
    : avatarSource.slice(0, 1).toUpperCase();

  function reset() {
    setDisplayName(initialName);
    setLocale(initialLocale);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      // `PATCH /v1/users/me` takes both fields in one call. The page used to
      // send only the locale and carried a banner saying this endpoint was
      // pending, months after it shipped.
      await identity.updateProfile({ display_name: displayName.trim() || null, locale });
      await refresh();
      notifySuccess(t('settings.profile.toast.saved'), t('settings.profile.toast.savedTitle'));
    } catch (err) {
      notifyError(err instanceof Error ? err.message : t('common.unknownError'), t('settings.profile.toast.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Stack gap="lg">
      {/* The eyebrow names the section this page belongs to, which is what the
          settings shell used to say with a header of its own above every
          child. One header per page, so the title on screen is the page. */}
      <PageHeader
        eyebrow={t('common.settings')}
        title={t('settings.profile.title')}
        description={t('settings.profile.description')}
      />

      <Card withBorder>
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={5}>{t('settings.profile.identity.title')}</Title>
            <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('settings.profile.identity.subtitle')}</Text>
          </Stack>

          {/*
            Four facts about the signed-in account, read-only because none of
            them can be changed from here: the address is the sign-in
            identifier, the organisation is the platform tenant and the role
            comes from the `scope` claim on the token.
          */}
          <div className="rr-profile-identity">
            <Avatar
              size="lg"
              radius="xl"
              style={{ background: 'var(--rr-color-primary)', color: 'var(--rr-color-primary-text)', fontWeight: 700 }}
            >
              {initials}
            </Avatar>
            <dl className="rr-profile-facts">
              <Fact label={t('settings.profile.identity.email')}>
                <Text role="bodySmall">{user?.email ?? t('common.none')}</Text>
              </Fact>
              <Fact label={t('settings.profile.identity.displayName')}>
                <Text role="bodySmall" c={user?.display_name ? undefined : 'var(--rr-color-text-muted)'}>
                  {user?.display_name || t('settings.profile.noDisplayName')}
                </Text>
              </Fact>
              <Fact label={t('settings.profile.identity.organisation')}>
                <Text role="bodySmall">{user?.org.name ?? t('common.none')}</Text>
              </Fact>
              <Fact label={t('settings.profile.identity.scope')}>
                <Stack gap="xs">
                  <StatusBadge status={user?.scope === 'platform' ? 'healthy' : 'pending'}>
                    {user?.scope === 'platform' ? t('settings.profile.scope.platform') : t('settings.profile.scope.tenant')}
                  </StatusBadge>
                  <Text role="caption" c="var(--rr-color-text-muted)">
                    {user?.scope === 'platform' ? t('settings.profile.scope.platformHint') : t('settings.profile.scope.tenantHint')}
                  </Text>
                </Stack>
              </Fact>
            </dl>
          </div>
        </Stack>
      </Card>

      <Card withBorder>
        <form onSubmit={handleSubmit}>
          <Stack gap="md">
            <Stack gap="xs">
              <Title order={5}>{t('settings.profile.details.title')}</Title>
              <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('settings.profile.details.subtitle')}</Text>
            </Stack>
            <TextInput
              label={t('settings.profile.displayName.label')}
              description={t('settings.profile.displayName.hint')}
              value={displayName}
              onChange={(e) => setDisplayName(e.currentTarget.value)}
              placeholder={t('settings.profile.displayName.placeholder')}
            />
            <Select
              label={t('settings.profile.locale.label')}
              description={t('settings.profile.locale.hint')}
              value={locale}
              onChange={(e) => setLocale(e.currentTarget.value)}
              data={LOCALES.map((l) => ({ value: l, label: t(`settings.profile.locale.options.${l}`) }))}
            />

            {/*
              The form's own footer bar. Save on its own left the operator with
              no way to back out of a half-typed change and no acknowledgement
              once the change had landed, so both live here: Reset undoes,
              and the right-hand side either warns that the edit is unsaved or
              confirms that it is not.
            */}
            <div className="rr-profile-formbar">
              <Group gap="sm" align="center" wrap="wrap">
                <Button type="button" variant="default" onClick={reset} disabled={!dirty || busy}>
                  {t('settings.profile.form.reset')}
                </Button>
                {dirty && (
                  <Text role="caption" c="var(--rr-color-text-muted)">{t('settings.profile.form.unsaved')}</Text>
                )}
              </Group>
              <Group gap="sm" align="center" wrap="wrap">
                {!dirty && !busy && (
                  <Text role="caption" c="var(--rr-color-success)">
                    <span aria-hidden>✓</span> {t('settings.profile.form.saved')}
                  </Text>
                )}
                <Button type="submit" loading={busy} disabled={!dirty}>{t('settings.profile.form.save')}</Button>
              </Group>
            </div>
          </Stack>
        </form>
      </Card>

      <NotificationPrefsPanel />
    </Stack>
  );
}

/** One label-and-value pair in the identity grid. */
function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="rr-profile-facts__row">
      <dt><Text role="bodySmall" c="var(--rr-color-text-muted)">{label}</Text></dt>
      <dd>{children}</dd>
    </div>
  );
}
